import React, { useState, useEffect, useRef, forwardRef } from 'react';
import Select from 'react-select';
import PropTypes from 'prop-types';
import {
  FormGroup,
  Input,
  Row,
  Col,
  Label,
  Container,
  CustomInput,
  ButtonGroup,
  Button,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleUp, faSearch } from '@fortawesome/free-solid-svg-icons';
import DatePicker from 'react-datepicker';
import { MobileView, isMobile, browserName, isAndroid } from 'react-device-detect';
import { getPaginateSourcesApi } from '../../services/Form/getSourcesApi';
import Block from '../common/Display/Block';
import '../../assets/scss/react-datepicker.css';
import Translate, { t, translateDate } from '../common/Translate';
import { useFilters } from '../../hooks/useForm/useFilters';
import MenuNavBottom from '../common/MenuNavBottom';
import { useFormSearch } from '../../hooks/useForm/useFormSearch';
import { useEditAppointment } from '../../hooks/useCalendar/useRecordBlocs/useEditAppointment';
import { getPaginateConcessionsApi } from '../../services/Form/getConcessionsApi';
import RecordsResults from './RecordsResults';
import { Auth } from '../../config/configuration/react';
import { NRM_SUB_STATUS as nrmStatusListConfig } from '../../config';
import AutobizStrapPopup from '../common/Modal/AutobizStrapPopup';

const DatePickerPopUp = forwardRef(({ filters, isOpen, toggle, handleValidate }, ref) => {
  const { creationDateMin, creationDateMax } = filters;

  const onChange = (dates) => {
    const [startDate, endDate] = dates;
    creationDateMax.setValue(endDate);
    creationDateMin.setValue(startDate);
  };

  useEffect(() => {
    function handler(event) {
      if (ref.current?.contains(event.target)) toggle();
    }
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  });

  return (
    <>
      {isOpen && (
        <div className="calendar-box">
          <DatePicker
            selected={creationDateMin?.value}
            startDate={creationDateMin?.value}
            endDate={creationDateMax?.value}
            locale={creationDateMin.locale}
            monthsShown={2}
            selectsRange
            onChange={onChange}
            inline
          />
          <div className="calendar-box-footer">
            <span className="label">
              {creationDateMin.value && creationDateMax.value
                ? `${translateDate(creationDateMin.value, 'DD/MM/YYYY')}-${translateDate(
                    creationDateMax.value,
                    'DD/MM/YYYY',
                  )}`
                : ''}
            </span>
            <button className="btn btn-3 ml-1" onClick={toggle} type="button">
              <Translate code="cancle" />
            </button>
            <button
              className="btn btn-1 shadow ml-1"
              onClick={handleValidate}
              type="button"
              disabled={!(creationDateMin.value && creationDateMax.value)}
            >
              <Translate code="exportData" />
            </button>
          </div>
        </div>
      )}
    </>
  );
});

DatePickerPopUp.propTypes = {
  filters: PropTypes.shape().isRequired,
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  handleValidate: PropTypes.func.isRequired,
};

const FormSearch = (props) => {
  const {
    records,
    goToPage,
    pageCount,
    currentPage,
    loading,
    error,
    showCta,
    showPaginnation,
    setSort,
    sort,
    showExportCta,
    selectedPos,
    changePos,
  } = useFormSearch();
  const { filters, searchWithFilters, resetFilters, mode, isOpen, toggle, handleValidate } = useFilters();
  const [filtersPanelOpen, setFiltersPanelOpen] = useState(false);
  const [isOpenMoreFields, setIsOpenMoreFields] = useState(false);
  const downloadCta = useRef(null);

  const toggleCollapse = () => {
    setIsOpenMoreFields(!isOpenMoreFields);
  };

  useEffect(() => {
    if (isMobile) {
      if (filtersPanelOpen !== false) {
        const container = document.getElementById('filters-container');
        if (container.classList.contains('open')) {
          const wrap = document.getElementById('wrap-container');
          if (wrap) wrap.setAttribute('style', 'display: block');
          document.addEventListener('mousedown', (event) => {
            if (event.target.id === 'wrap-container' && filtersPanelOpen) {
              wrap.setAttribute('style', 'display: none');
              setFiltersPanelOpen(false);
              document.removeEventListener('mousedown', () => {});
            }
          });
        }
      } else {
        const wrap = document.getElementById('wrap-container');
        if (wrap.hasAttribute('style', 'display: block')) wrap.removeAttribute('style', 'display: block');
      }

      document.addEventListener('scroll', () => {
        const btn = document.getElementById('search-cta-fixed');
        const container = document.getElementById('filters-container');
        if (window.scrollY > 0) {
          if (btn && !btn.classList.contains('show-cta')) btn.classList.add('show-cta');
          if (container) document.getElementById('filters-container').setAttribute('style', 'top: 0');
          if (isAndroid && browserName === 'Firefox')
            document.getElementById('pagination').style.paddingBottom = '50px';
        } else if (container && container.hasAttribute('style', 'top: 0')) container.removeAttribute('style', 'top: 0');
      });

      if (document.readyState === 'complete') {
        const filtersTab = document.getElementById('container-fluid');
        const btn = document.getElementById('search-cta-fixed');
        if (isAndroid && browserName === 'Firefox' && filtersTab)
          filtersTab.setAttribute('style', 'padding-bottom: 50px');
        if (showCta && btn) btn.classList.add('show-cta');
      }
    }
  });

  return (
    <Block {...props} noBack>
      <AutobizStrapPopup />
      <h1 className="page-title">
        <span className="cursor-pointer">
          <FontAwesomeIcon icon={faSearch} /> {t('search')}
        </span>
        {!isMobile && showExportCta && (
          <button type="button" className="btn refresh-button btn-3" ref={downloadCta}>
            <Translate code="exportData" />
          </button>
        )}
      </h1>
      <DatePickerPopUp
        filters={filters}
        isOpen={isOpen}
        toggle={toggle}
        handleValidate={handleValidate}
        ref={downloadCta}
      />
      <div className="filters-results-container" id="filters-results-container">
        <Filters
          filters={filters}
          searchWithFilters={searchWithFilters}
          resetFilters={resetFilters}
          mode={mode}
          filtersPanelOpen={filtersPanelOpen}
          setFiltersPanelOpen={setFiltersPanelOpen}
          toggleCollapse={toggleCollapse}
          isOpenMoreFields={isOpenMoreFields}
          selectedPos={selectedPos}
          changePos={changePos}
        />
        <RecordsResults
          currentPage={currentPage}
          pageNumbers={pageCount}
          changePaginationIndex={goToPage}
          loading={loading}
          forms={records}
          error={error}
          origin="search"
          setFiltersPanelOpen={setFiltersPanelOpen}
          filtersPanelOpen={filtersPanelOpen}
          showPaginnation={showPaginnation}
          setSort={setSort}
          sort={sort}
        />
      </div>

      <MobileView>
        <div className="text-center search-cta-sticky" id="test">
          <button type="button" className="btn btn-1" onClick={() => setFiltersPanelOpen(!filtersPanelOpen)}>
            {t('search_ctaa')}
          </button>
        </div>
      </MobileView>
      <MenuNavBottom active="search" />
    </Block>
  );
};

export default FormSearch;

const Filters = ({
  filters,
  searchWithFilters,
  mode,
  resetFilters,
  filtersPanelOpen,
  setFiltersPanelOpen,
  toggleCollapse,
  isOpenMoreFields,
  selectedPos,
  changePos,
  recordId,
  concessionName,
  concessionId: propConcessionId,
  appointmentDateTime,
  toggle,
  counterInspection,
  recordTypeId: propRecordTypeId,
}) => {
  const {
    creationDateMin,
    creationDateMax,
    entryStockDateMin,
    entryStockDateMax,
    refHexaId,
    registration,
    vin,
    registrationDate,
    concessionId,
    make,
    model,
    userId,
    status,
    clic2sell,
    clic2buy,
    recordTypeId,
    guaranteedPrice,
    sourceId,
    nrmStatus,
    nrmRecordId,
    cancelCases,
    carcheck,
    stellaClub,
    pushToCoverage,
    pushToStockSale,
  } = filters;
  const {
    searchTextSource,
    searchTextConcession,
    setSearchTextConcession,
    setSearchTextSource,
    searchSource,
    sourceList,
    setSourceList,
    sourceListLoading,
    setSourceListLoading,
    currentPageSource,
    setCurrentPageSource,
    offset,
    setOffset,
    totalPages,
    setTotalPages,
    postsPerPage,
    setPostPerPage,
  } = useFormSearch();

  const {
    handleScroll,
    concessionOptions,
    setConcessionOptions,
    pointOfSaleListLoading,
    setPointOfSale,
    searchConcession,
  } = useEditAppointment({
    recordId,
    concessionId: propConcessionId,
    concessionName,
    appointmentDateTime,
    toggle,
    counterInspection,
    recordTypeId: propRecordTypeId,
  });

  const [selectedOptionSource, setSelectedOptionSource] = useState(null);
  const [selectedOptionConcession, setSelectedOptionConcession] = useState(null);
  const [sourceDropdownOpen, setSourceDropdownOpen] = useState(false);
  const [concessionDropdownOpen, setConcessionDropdownOpen] = useState(false);
  const isAtBottom = useRef(false);
  const toggleSourceDropdown = () => setSourceDropdownOpen((prevState) => !prevState);
  const toggleConcessionDropdown = () => setConcessionDropdownOpen((prevState) => !prevState);
  const [listConcessions, setListConcessions] = useState([]);
  const [listSources, setListSources] = useState([]);
  const isNrmUser = Auth.isNrmUser();
  const isCarcheckUser = Auth.isCarcheckUser();
  const isSolContract = Auth.isSolContract();
  useEffect(() => {
    if (concessionOptions.length && !listConcessions.length) {
      setListConcessions(concessionOptions);
    }
  }, [concessionOptions]);

  useEffect(() => {
    if (sourceList.length && !listSources.length) {
      setListSources(sourceList);
    }
  }, [sourceList]);

  const search = async () => {
    setFiltersPanelOpen(false);
    await searchWithFilters();
  };

  const keyPress = (e) => {
    if (e.keyCode === 13 && !isMobile) {
      search();
    }
  };
  const hideKeybord = (e) => {
    if (isMobile) {
      e.target.blur();
    }
  };

  const isEmpty = (value) => !value || value.length === 0;

  const getPaginateSources = async (isMounted = false) => {
    if (totalPages && currentPageSource > totalPages) return;
    setSourceListLoading(true);
    getPaginateSourcesApi({ offset, postsPerPage, source: searchTextSource })
      .then((res) => {
        if (isMounted) return;
        setSourceList((perv) => [...perv, ...res.list]);
        setOffset((perv) => perv + 100);
        setPostPerPage((perv) => perv + 100);
        setCurrentPageSource((perv) => perv + 1);
        setTotalPages(Math.ceil(res.total / 100));
      })
      .finally(() => {
        setSourceListLoading(false);
      });
  };

  useEffect(() => {
    let isMounted = false;
    getPaginateSources(isMounted);
    return () => {
      isMounted = true;
    };
  }, []);

  const getSourceOptions = () => {
    if (sourceList.length) {
      return [
        { value: null, label: sourceId.input.label, selected: false },
        ...sourceList.map((c) => ({
          value: c.id,
          label: c.source,
        })),
      ];
    }
    return [];
  };

  const handleScrollSource = (event) => {
    const dropdownMenu = event.target;
    const isAtBottomNow =
      Math.ceil(dropdownMenu.scrollTop) + Math.ceil(dropdownMenu.offsetHeight) >= Math.ceil(dropdownMenu.scrollHeight);
    if (isAtBottomNow && !isAtBottom.current && !sourceListLoading) {
      getPaginateSources();
      isAtBottom.current = true;
    } else if (!isAtBottomNow) {
      isAtBottom.current = false;
    }
  };

  const updateConcessions = async (selectedSourceId) => {
    setConcessionOptions([]);
    setPointOfSale(true);

    try {
      const response = await getPaginateConcessionsApi(
        { value: selectedSourceId },
        { offset: 0, postsPerPage: 100, name: '' },
      );
      const updatedConcessions = [{ id: null, name: concessionId.input.label }, ...response.list];
      setConcessionOptions(updatedConcessions);
    } catch (error) {
      console.error('Error fetching concessions:', error);
    } finally {
      setPointOfSale(false);
    }
  };

  const handleChangeSource = async (e) => {
    const newValue = parseInt(e?.value, 10);
    sourceId.setValue(newValue);
    setSelectedOptionSource(e);
    toggleSourceDropdown();
    setSearchTextSource('');
    setSearchTextConcession('');
    if (newValue) {
      await updateConcessions(newValue);
    } else {
      setConcessionOptions(listConcessions);
      setSourceList(listSources);
    }
  };

  useEffect(() => {
    if (!selectedOptionSource?.value) {
      setSelectedOptionConcession(null);
      setSearchTextConcession('');
    }
  }, [selectedOptionSource]);

  const getConcessionOptions = () => {
    if (concessionOptions.length && !concessionOptions.some((concession) => concession.id === null)) {
      concessionOptions.unshift({ id: null, name: concessionId.input.label, selected: false });
    }
    if (selectedOptionSource?.value) {
      if (selectedOptionConcession?.value) {
        const filterConcessionsBySource = concessionOptions.some(
          (concession) => concession.id === selectedOptionConcession.value,
        );

        if (!filterConcessionsBySource) {
          setSelectedOptionConcession(null);
        }
      }
    }

    return concessionOptions.map((concession) => ({
      value: concession.id,
      label: concession.name,
    }));
  };

  const handleChangeConcession = (e) => {
    concessionId.setValue(e?.value);
    setSelectedOptionConcession(e);
    toggleConcessionDropdown();
    setSearchTextSource('');
    setSearchTextConcession('');
  };

  return (
    <>
      <div className="wrap-container" id="wrap-container" />
      <div className={`filters-container${filtersPanelOpen ? ' open' : ''}`} id="filters-container">
        <Container fluid id="container-fluid">
          {isNrmUser && (
            <Row className="mb-1">
              <Col xs={12} sm={6} lg={12}>
                <ButtonGroup className="two-tap-button">
                  {['privatePos', 'publicPos'].map((tab) => (
                    <Button onClick={() => changePos(tab)} className={tab === selectedPos && 'active'}>
                      {t(tab)}
                    </Button>
                  ))}
                </ButtonGroup>
              </Col>
            </Row>
          )}
          {(selectedPos === 'publicPos' && (
            <Row>
              <Col xs={12} sm={6} lg={12}>
                <FormGroup>
                  <Label for={refHexaId.input.id}>{refHexaId.label}</Label>
                  <Input
                    placeholder={refHexaId.placeholder}
                    className="rounded"
                    type="text"
                    name={refHexaId.input.name}
                    id={refHexaId.input.id}
                    onChange={(e) => refHexaId.setValue(e.target.value)}
                    value={refHexaId.value}
                    onKeyDown={(e) => keyPress(e)}
                  />
                </FormGroup>
              </Col>
              <Col xs={12} sm={6} lg={12}>
                <FormGroup>
                  <Label for={nrmRecordId.input.id}>{nrmRecordId.label}</Label>
                  <Input
                    placeholder={nrmRecordId.placeholder}
                    className="rounded"
                    type="text"
                    name={nrmRecordId.input.name}
                    id={nrmRecordId.input.id}
                    onChange={(e) => nrmRecordId.setValue(e.target.value)}
                    value={nrmRecordId.value}
                    onKeyDown={(e) => keyPress(e)}
                  />
                </FormGroup>
              </Col>
              {mode === 'registration' && (
                <Col xs={12} sm={6} lg={12}>
                  <FormGroup>
                    <Label for={registration.input.id}>{registration.label}</Label>
                    <Input
                      placeholder={registration.placeholder}
                      className="form-control rounded"
                      type="text"
                      name={registration.input.name}
                      id={registration.input.id}
                      onChange={(e) => registration.setValue(e.target.value)}
                      value={registration.value}
                      onKeyDown={(e) => keyPress(e)}
                    />
                  </FormGroup>
                </Col>
              )}
              {mode === 'vin' && (
                <Col xs={12} sm={6} lg={12}>
                  <FormGroup>
                    <Label for={vin.input.id}>{vin.label}</Label>
                    <Input
                      placeholder={vin.placeholder}
                      className="rounded"
                      type="text"
                      name={vin.input.name}
                      id={vin.input.id}
                      onChange={(e) => vin.setValue(e.target.value)}
                      value={vin.value}
                      onKeyDown={(e) => keyPress(e)}
                    />
                  </FormGroup>
                </Col>
              )}
            </Row>
          )) || (
            <Row>
              <Col xs={12} sm={6} lg={12}>
                <FormGroup>
                  <Label for={creationDateMin.input.id}>{creationDateMin.label}</Label>
                  <span className="text-danger">
                    <small>{creationDateMin.error}</small>
                  </span>
                  <DatePicker
                    className="rounded"
                    name={creationDateMin.input.name}
                    id={creationDateMin.input.id}
                    onChange={(date) => creationDateMin.setValue(date)}
                    selected={creationDateMin.value}
                    locale={creationDateMin.locale}
                    dateFormat={creationDateMin.dateFormat}
                    placeholderText={creationDateMin.placeholder}
                    autoComplete="off"
                    onFocus={(e) => hideKeybord(e)}
                  />
                </FormGroup>
              </Col>
              <Col xs={12} sm={6} lg={12} className="mt-10">
                <FormGroup>
                  <Label for={creationDateMax.input.id}>{creationDateMax.label}</Label>
                  <span className="text-danger">
                    <small>{creationDateMax.error}</small>
                  </span>
                  <DatePicker
                    className="rounded"
                    name={creationDateMax.input.name}
                    id={creationDateMax.input.id}
                    onChange={(date) => creationDateMax.setValue(date)}
                    selected={creationDateMax.value}
                    locale={creationDateMax.locale}
                    dateFormat={creationDateMax.dateFormat}
                    placeholderText={creationDateMax.placeholder}
                    autoComplete="off"
                    onFocus={(e) => hideKeybord(e)}
                  />
                </FormGroup>
              </Col>
              <Col xs={12} sm={6} lg={12}>
                <FormGroup>
                  <Label for={entryStockDateMin.input.id}>{entryStockDateMin.label}</Label>
                  <span className="text-danger">
                    <small>{entryStockDateMin.error}</small>
                  </span>
                  <DatePicker
                    className="rounded"
                    name={entryStockDateMin.input.name}
                    id={entryStockDateMin.input.id}
                    onChange={(date) => entryStockDateMin.setValue(date)}
                    selected={entryStockDateMin.value}
                    locale={entryStockDateMin.locale}
                    dateFormat={entryStockDateMin.dateFormat}
                    placeholderText={entryStockDateMin.placeholder}
                    autoComplete="off"
                    onFocus={(e) => hideKeybord(e)}
                  />
                </FormGroup>
              </Col>
              <Col xs={12} sm={6} lg={12} className="mt-10">
                <FormGroup>
                  <Label for={entryStockDateMax.input.id}>{entryStockDateMax.label}</Label>
                  <span className="text-danger">
                    <small>{entryStockDateMax.error}</small>
                  </span>
                  <DatePicker
                    className="rounded"
                    name={entryStockDateMax.input.name}
                    id={entryStockDateMax.input.id}
                    onChange={(date) => entryStockDateMax.setValue(date)}
                    selected={entryStockDateMax.value}
                    locale={entryStockDateMax.locale}
                    dateFormat={entryStockDateMax.dateFormat}
                    placeholderText={entryStockDateMax.placeholder}
                    autoComplete="off"
                    onFocus={(e) => hideKeybord(e)}
                  />
                </FormGroup>
              </Col>
              <Col xs={12} sm={6} lg={12}>
                <FormGroup>
                  <Label for={refHexaId.input.id}>{refHexaId.label}</Label>
                  <Input
                    placeholder={refHexaId.placeholder}
                    className="rounded"
                    type="text"
                    name={refHexaId.input.name}
                    id={refHexaId.input.id}
                    onChange={(e) => refHexaId.setValue(e.target.value)}
                    value={refHexaId.value}
                    onKeyDown={(e) => keyPress(e)}
                  />
                </FormGroup>
              </Col>
              {isNrmUser && !isCarcheckUser && (
                <Col xs={12} sm={6} lg={12}>
                  <FormGroup>
                    <Label for={nrmRecordId.input.id}>{nrmRecordId.label}</Label>
                    <Input
                      placeholder={nrmRecordId.placeholder}
                      className="rounded"
                      type="text"
                      name={nrmRecordId.input.name}
                      id={nrmRecordId.input.id}
                      onChange={(e) => nrmRecordId.setValue(e.target.value)}
                      value={nrmRecordId.value}
                      onKeyDown={(e) => keyPress(e)}
                    />
                  </FormGroup>
                </Col>
              )}
              {mode === 'registration' && (
                <Col xs={12} sm={6} lg={12}>
                  <FormGroup>
                    <Label for={registration.input.id}>{registration.label}</Label>
                    <Input
                      placeholder={registration.placeholder}
                      className="form-control rounded"
                      type="text"
                      name={registration.input.name}
                      id={registration.input.id}
                      onChange={(e) => registration.setValue(e.target.value)}
                      value={registration.value}
                      onKeyDown={(e) => keyPress(e)}
                    />
                  </FormGroup>
                </Col>
              )}
              {mode === 'vin' && (
                <Col xs={12} sm={6} lg={12}>
                  <FormGroup>
                    <Label for={vin.input.id}>{vin.label}</Label>
                    <Input
                      placeholder={vin.placeholder}
                      className="rounded"
                      type="text"
                      name={vin.input.name}
                      id={vin.input.id}
                      onChange={(e) => vin.setValue(e.target.value)}
                      value={vin.value}
                      onKeyDown={(e) => keyPress(e)}
                    />
                  </FormGroup>
                </Col>
              )}
              {isCarcheckUser && (
                <>
                  <Col xs={12} sm={6} lg={12}>
                    <FormGroup>
                      <Label for={make.input.id}>{make.label}</Label>
                      <Input
                        placeholder={make.placeholder}
                        className="rounded"
                        type="select"
                        name={make.input.name}
                        id={make.input.id}
                        onChange={(e) => make.setValue(e.target.value)}
                        value={make.value}
                      >
                        <option value="">{make.input.label}</option>
                        {make.options &&
                          make.options.map((m) => (
                            <option key={m.id} value={m.name}>
                              {m.name}
                            </option>
                          ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col xs={12} sm={6} lg={12}>
                    <FormGroup>
                      <Label for={model.input.id}>{model.label}</Label>
                      <Input
                        placeholder={model.placeholder}
                        className="rounded"
                        type="select"
                        name={model.input.name}
                        id={model.input.id}
                        onChange={(e) => model.setValue(e.target.value)}
                        value={model.value}
                      >
                        <option value="">{model.input.label}</option>
                        {model.options &&
                          model.options.map((m) => (
                            <option key={m.id} value={m.name}>
                              {m.name}
                            </option>
                          ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col xs={12} sm={6} lg={12}>
                    <FormGroup>
                      <Label for={registrationDate.input.id}>{registrationDate.label}</Label>
                      <Input
                        className="rounded"
                        type="number"
                        name={registrationDate.input.name}
                        id={registrationDate.input.id}
                        placeholder={registrationDate.placeholder}
                        onChange={(e) => registrationDate.setValue(e.target.value)}
                        value={registrationDate.value}
                        onKeyDown={(e) => keyPress(e)}
                      />
                    </FormGroup>
                  </Col>
                </>
              )}
              <Col xs={12} sm={6} lg={12}>
                <FormGroup>
                  <Label for={sourceId.input.id}>{sourceId.label}</Label>
                  <Dropdown isOpen={sourceDropdownOpen} toggle={toggleSourceDropdown} onScroll={handleScrollSource}>
                    <DropdownToggle
                      caret
                      className="form-control drop-down-pos rounded custom-dropdown-toggle"
                      data-testid={sourceId.input.id}
                      id={sourceId.input.id}
                    >
                      {selectedOptionSource ? selectedOptionSource?.label : sourceId.input.label}
                    </DropdownToggle>
                    <DropdownMenu className="dropdown-menu custom-dropdown-menu">
                      <Select
                        id={sourceId.input.id}
                        value={sourceId.value}
                        inputValue={searchTextSource}
                        onChange={handleChangeSource}
                        options={getSourceOptions()}
                        isSearchable
                        menuIsOpen
                        isLoading={sourceListLoading}
                        onInputChange={(value, action) => {
                          if (action.action === 'input-change') {
                            setSearchTextSource(value);
                            searchSource(value);
                          }
                        }}
                      />
                    </DropdownMenu>
                  </Dropdown>
                </FormGroup>
              </Col>
              <Col xs={12} sm={6} lg={12}>
                <FormGroup>
                  <Label for={concessionId.input.id}>{concessionId.label}</Label>
                  <Dropdown isOpen={concessionDropdownOpen} toggle={toggleConcessionDropdown} onScroll={handleScroll}>
                    <DropdownToggle
                      caret
                      className="form-control drop-down-pos rounded custom-dropdown-toggle"
                      data-testid={concessionId.input.id}
                      id={concessionId.input.id}
                    >
                      {selectedOptionConcession ? selectedOptionConcession?.label : concessionId.input.label}
                    </DropdownToggle>
                    <DropdownMenu className="dropdown-menu custom-dropdown-menu">
                      <Select
                        id={concessionId.input.id}
                        value={concessionId.value}
                        inputValue={searchTextConcession}
                        onChange={handleChangeConcession}
                        options={getConcessionOptions()}
                        isSearchable
                        menuIsOpen
                        isLoading={pointOfSaleListLoading}
                        onInputChange={(value, action) => {
                          if (action.action === 'input-change') {
                            setSearchTextConcession(value);
                            if (selectedOptionSource) {
                              searchConcession(value, selectedOptionSource);
                            } else {
                              searchConcession(value);
                            }
                          }
                        }}
                      />
                    </DropdownMenu>
                  </Dropdown>
                </FormGroup>
              </Col>
              <Col xs={12} sm={6} lg={12}>
                <FormGroup>
                  <Label for={userId.input.id}>{userId.label}</Label>
                  <Input
                    placeholder={userId.placeholder}
                    className="rounded"
                    type="select"
                    name={userId.input.name}
                    id={userId.input.id}
                    onChange={(e) => userId.setValue(e.target.value)}
                    value={userId.value}
                  >
                    <option value="">{userId.input.label}</option>
                    {userId.options.map((buyer) => (
                      <option key={buyer.id} value={buyer.id}>
                        {buyer.name}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              {isNrmUser && !isCarcheckUser && (
                <>
                  <Col xs={12} sm={6} lg={12}>
                    <FormGroup>
                      <Label for={nrmStatus.input.id}>{nrmStatus.label}</Label>
                      <Input
                        placeholder={nrmStatus.placeholder}
                        className="rounded"
                        type="select"
                        name={nrmStatus.input.name}
                        id={nrmStatus.input.id}
                        onChange={(e) => {
                          nrmStatus.setValue(e.target.value);
                          if (Number(e.target.value !== nrmStatusListConfig.tradeInCancelled)) cancelCases.setValue('');
                        }}
                        value={nrmStatus.value}
                      >
                        <option value="">{nrmStatus.input.label}</option>
                        {nrmStatus.options.map((s) => (
                          <option key={s.id} value={s.id}>
                            {t(s.name)}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  {Number(nrmStatus.value) === nrmStatusListConfig.tradeInCancelled && (
                    <Col xs={12} sm={6} lg={12}>
                      <FormGroup>
                        <Label for={cancelCases.input.id}>{cancelCases.label}</Label>
                        <Input
                          placeholder={cancelCases.placeholder}
                          className="rounded"
                          type="select"
                          name={cancelCases.input.name}
                          id={cancelCases.input.id}
                          onChange={(e) => cancelCases.setValue(e.target.value)}
                          value={cancelCases.value}
                        >
                          <option value="">{cancelCases.input.label}</option>
                          {cancelCases.options.map((s) => (
                            <option key={s.id} value={s.id}>
                              {t(s.name)}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                  )}
                </>
              )}
              {isCarcheckUser && (
                <Col xs={12} sm={6} lg={12}>
                  <FormGroup>
                    <Label for={status.input.id}>{status.label}</Label>
                    <Input
                      placeholder={status.placeholder}
                      className="rounded"
                      type="select"
                      name={status.input.name}
                      id={status.input.id}
                      onChange={(e) => status.setValue(e.target.value)}
                      value={status.value}
                    >
                      <option value="">{status.input.label}</option>
                      {status.options.map((s) => (
                        <option key={s.id} value={s.id}>
                          {t(s.name)}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
              )}
              {isNrmUser && isCarcheckUser && (
                <>
                  <Col xs={12} sm={6} lg={12}>
                    <button type="button" onClick={toggleCollapse} className="more-fields-toggle-btn px-0 pb-1">
                      <Translate code="displayMoreNrmField " />
                      {isOpenMoreFields ? (
                        <span className="cursor-pointer">
                          <FontAwesomeIcon icon={faAngleUp} />
                        </span>
                      ) : (
                        <span className="cursor-pointer">
                          <FontAwesomeIcon icon={faAngleDown} />
                        </span>
                      )}
                    </button>
                  </Col>
                  {isOpenMoreFields && (
                    <>
                      <Col xs={12} sm={6} lg={12}>
                        <FormGroup>
                          <Label for={nrmRecordId.input.id}>{nrmRecordId.label}</Label>
                          <Input
                            placeholder={nrmRecordId.placeholder}
                            className="rounded"
                            type="text"
                            name={nrmRecordId.input.name}
                            id={nrmRecordId.input.id}
                            onChange={(e) => nrmRecordId.setValue(e.target.value)}
                            value={nrmRecordId.value}
                            onKeyDown={(e) => keyPress(e)}
                          />
                        </FormGroup>
                      </Col>
                      <Col xs={12} sm={6} lg={12}>
                        <FormGroup>
                          <Label for={nrmStatus.input.id}>{nrmStatus.label}</Label>
                          <Input
                            placeholder={nrmStatus.placeholder}
                            className="rounded"
                            type="select"
                            name={nrmStatus.input.name}
                            id={nrmStatus.input.id}
                            onChange={(e) => {
                              nrmStatus.setValue(e.target.value);
                              if (Number(e.target.value !== nrmStatusListConfig.tradeInCancelled))
                                cancelCases.setValue('');
                            }}
                            value={nrmStatus.value}
                          >
                            <option value="">{nrmStatus.input.label}</option>
                            {nrmStatus.options.map((s) => (
                              <option key={s.id} value={s.id}>
                                {t(s.name)}
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>
                      {Number(nrmStatus.value) === nrmStatusListConfig.tradeInCancelled && (
                        <Col xs={12} sm={6} lg={12}>
                          <FormGroup>
                            <Label for={cancelCases.input.id}>{cancelCases.label}</Label>
                            <Input
                              placeholder={cancelCases.placeholder}
                              className="rounded"
                              type="select"
                              name={cancelCases.input.name}
                              id={cancelCases.input.id}
                              onChange={(e) => cancelCases.setValue(e.target.value)}
                              value={cancelCases.value}
                            >
                              <option value="">{cancelCases.input.label}</option>
                              {cancelCases.options.map((s) => (
                                <option key={s.id} value={s.id}>
                                  {t(s.name)}
                                </option>
                              ))}
                            </Input>
                          </FormGroup>
                        </Col>
                      )}
                    </>
                  )}
                </>
              )}
              {isCarcheckUser && (
                <Col xs={12} sm={6} lg={12}>
                  <CustomInput
                    type="switch"
                    id={carcheck.input.id}
                    name={carcheck.input.name}
                    label={carcheck.label}
                    checked={carcheck.value}
                    onChange={(e) => {
                      carcheck.setValue(e.target.checked);
                      clic2sell.setValue(false);
                      stellaClub.setValue(false);
                    }}
                  />
                </Col>
              )}

              <Col xs={12} sm={6} lg={12}>
                <CustomInput
                  type="switch"
                  id={clic2sell.input.id}
                  name={clic2sell.input.name}
                  label={clic2sell.label}
                  checked={clic2sell.value}
                  onChange={(e) => {
                    clic2sell.setValue(e.target.checked);
                    carcheck.setValue(false);
                    stellaClub.setValue(false);
                  }}
                />
              </Col>
              {isCarcheckUser && (
                <>
                  <Col xs={12} sm={6} lg={12}>
                    <CustomInput
                      type="switch"
                      id={clic2buy.input.id}
                      name={clic2buy.input.name}
                      label={clic2buy.label}
                      checked={clic2buy.value}
                      onChange={(e) => clic2buy.setValue(e.target.checked)}
                    />
                  </Col>
                  <Col xs={12} sm={6} lg={12}>
                    <CustomInput
                      type="switch"
                      id={recordTypeId.input.id}
                      name={recordTypeId.input.name}
                      label={recordTypeId.label}
                      checked={recordTypeId.value}
                      onChange={(e) => recordTypeId.setValue(e.target.checked)}
                    />
                  </Col>
                  <Col xs={12} sm={6} lg={12}>
                    <CustomInput
                      type="switch"
                      id={guaranteedPrice.input.id}
                      name={guaranteedPrice.input.name}
                      label={guaranteedPrice.label}
                      checked={guaranteedPrice.value}
                      onChange={(e) => guaranteedPrice.setValue(e.target.checked)}
                    />
                  </Col>
                </>
              )}
              {isSolContract && (
                <Col xs={12} sm={6} lg={12}>
                  <CustomInput
                    type="switch"
                    id={stellaClub.input.id}
                    name={stellaClub.input.name}
                    label={stellaClub.label}
                    checked={stellaClub.value}
                    onChange={(e) => {
                      stellaClub.setValue(e.target.checked);
                      clic2sell.setValue(false);
                      carcheck.setValue(false);
                    }}
                  />
                </Col>
              )}
              <Col xs={12} sm={6} lg={12}>
                <CustomInput
                  type="switch"
                  id={pushToCoverage.input.id}
                  name={pushToCoverage.input.name}
                  label={pushToCoverage.label}
                  checked={pushToCoverage?.value}
                  onChange={(e) => {
                    pushToCoverage.setValue(e.target.checked);
                  }}
                />
              </Col>
              <Col xs={12} sm={6} lg={12}>
                <CustomInput
                  type="switch"
                  id={pushToStockSale.input.id}
                  name={pushToStockSale.input.name}
                  label={pushToStockSale.label}
                  checked={pushToStockSale?.value}
                  onChange={(e) => {
                    pushToStockSale.setValue(e.target.checked);
                  }}
                />
              </Col>
            </Row>
          )}
          <Row className="mt-2">
            <Col xs={12} sm={6} lg={12}>
              <button
                className="btn btn-block text-center mb-2 btn-2"
                onClick={() => search()}
                data-cy="searchBtn"
                type="button"
                disabled={
                  selectedPos === 'publicPos' &&
                  isEmpty(registration.value) &&
                  isEmpty(vin.value) &&
                  isEmpty(nrmRecordId.value) &&
                  isEmpty(refHexaId.value)
                }
              >
                <Translate code="search_button_Label" />
              </button>
            </Col>
            <Col xs={12} sm={6} lg={12}>
              <button type="button" className="btn btn-block mb-2 reset_filters btn-3" onClick={() => resetFilters()}>
                <Translate code="reset_filters" />
              </button>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

Filters.propTypes = {
  filters: PropTypes.shape().isRequired,
  searchWithFilters: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  resetFilters: PropTypes.func.isRequired,
  filtersPanelOpen: PropTypes.bool.isRequired,
  setFiltersPanelOpen: PropTypes.func.isRequired,
  toggleCollapse: PropTypes.func.isRequired,
  isOpenMoreFields: PropTypes.bool.isRequired,
  selectedPos: PropTypes.string.isRequired,
  changePos: PropTypes.func.isRequired,
  recordId: PropTypes.number.isRequired,
  concessionName: PropTypes.string.isRequired,
  concessionId: PropTypes.number.isRequired,
  appointmentDateTime: PropTypes.string.isRequired,
  toggle: PropTypes.bool.isRequired,
  counterInspection: PropTypes.oneOfType([PropTypes.array, PropTypes.shape()]).isRequired,
  recordTypeId: PropTypes.number.isRequired,
};
